/**
 * RunTrend — one scenario's completed runs at a glance, oldest first.
 *
 * Each run is a small bar: its fill is the verdict tone (`bg-mark-*`), its
 * height the number of problems that run found, so a shrinking row of bars
 * reads as "getting better". Every bar is a link to its run with the full
 * reading as text ("Run 2: verdict Partial, 4 problems found, 2 still open"),
 * and the line beside it spells out what moved in words.
 */

import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import { toneDotClass, verdictLabel } from "@/components/experience/vocabulary";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  buildRunTrend,
  describeRunTrend,
  describeTrendPoint,
  type TrendRunInput,
} from "@/lib/run-trend";
import { cn } from "@/lib/utils";

/** Most recent runs drawn as bars; older ones are summarised as "+N". */
const MAX_MARKS = 12;

export function RunTrend({
  runs,
  projectId,
  runNumbers,
  className,
}: {
  runs: readonly TrendRunInput[];
  projectId: string;
  /** The surface's own "Run #N" numbering, so marks match the rows below. */
  runNumbers?: ReadonlyMap<string, number>;
  className?: string;
}) {
  const points = useMemo(
    () => buildRunTrend(runs, runNumbers),
    [runs, runNumbers],
  );
  const summary = describeRunTrend(points);
  // A trend needs two completed runs; a single run's row says it all.
  if (!summary) return null;

  const shown = points.slice(-MAX_MARKS);
  const hidden = points.length - shown.length;
  const maxProblems = Math.max(1, ...shown.map((p) => p.problems ?? 0));

  return (
    <div
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5",
        className,
      )}
    >
      <div className="flex shrink-0 items-center gap-1.5">
        {hidden > 0 && (
          <span className="text-xs tabular-nums text-muted-foreground">
            +{hidden}
            <span className="sr-only"> earlier runs</span>
          </span>
        )}
        <ol
          aria-label="Completed runs, oldest first"
          // Each mark's link is a 24px target (WCAG 2.5.8); the bar inside
          // stays narrow.
          className="flex h-6 items-end"
        >
          {shown.map((point) => {
            const label = describeTrendPoint(point);
            // Unknown counts draw full height; zero problems a thin sliver.
            const height =
              point.problems === null
                ? 100
                : point.problems === 0
                  ? 14
                  : 30 + (70 * point.problems) / maxProblems;
            return (
              <li key={point.id} className="flex h-full">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`/projects/${projectId}/walkthroughs/${point.id}`}
                      aria-label={label}
                      className="group flex h-full w-6 items-end justify-center rounded-xs p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-muted/50"
                    >
                      <div className="relative flex h-full w-2.5 items-end justify-center rounded-xs bg-muted/70 dark:bg-muted/40 transition-colors group-hover:bg-muted">
                        <span
                          aria-hidden="true"
                          className={cn(
                            "w-full rounded-xs transition-all",
                            toneDotClass(point.tone),
                          )}
                          style={{ height: `${height}%` }}
                        />
                      </div>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs py-1.5 px-2.5">
                    <div className="font-semibold text-foreground">
                      Run #{point.runNumber}
                    </div>
                    <div className="text-muted-foreground">
                      Verdict:{" "}
                      <span className="font-medium text-foreground">
                        {point.rating ? verdictLabel(point.rating) : "None"}
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      Problems:{" "}
                      <span className="font-medium text-foreground">
                        {point.problems === null
                          ? "Unknown"
                          : point.problems === 0
                            ? "0 found"
                            : `${point.problems} found`}
                      </span>
                      {point.problems !== null &&
                        point.problems > 0 &&
                        point.openProblems !== null && (
                          <span> ({point.openProblems} still open)</span>
                        )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </li>
            );
          })}
        </ol>
      </div>
      <p className="min-w-0 text-sm tabular-nums text-muted-foreground">
        <ArrowText text={summary} />
      </p>
    </div>
  );
}

/** Render "a → b" with the arrow read as "to" by screen readers. */
export function ArrowText({ text }: { text: string }) {
  const parts = text.split(" → ");
  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <>
              {" "}
              <span
                aria-hidden="true"
                className="mx-0.5 font-mono text-xs text-muted-foreground"
              >
                →
              </span>
              <span className="sr-only">to</span>{" "}
            </>
          )}
          {part}
        </Fragment>
      ))}
    </>
  );
}
